import React, { useContext, memo } from 'react';
import { string, shape, number, bool } from 'prop-types';
import StaticPropsContext from '../context/static-props';
import classNames from 'classnames';
import StyledLabel from '../styled-label';
import IconFactory from '../icons/factory-builder';
import MediaAction from '../media/media-action';
import Tooltip from '../tooltip';
import { trackEvent } from '../../lib/tracking';
import { getInspectionTooltipContent } from './components/inspection-tooltip-content';
import { getInspectionBadgeContent } from './components/inspection-badge-content';
import { getOffSetX } from './components/get-off-set-x';

const namespace = 'ui-pdp-inspection-vehicles-tag';
const MOBILE = 'mobile';

const defaultTagAction = {
  label: {
    text: null,
    color: null,
    bg_color: null,
    font_size: null,
    font_family: null,
    is_bank_discount: false,
  },
  target: null,
};

const InspectionVehiclesTag = ({
  tag_action = defaultTagAction,
  icon_action,
  additional_description,
  campaign_link,
}) => {
  const { andes_tooltip = null, icon = null } = icon_action;
  const { deviceType, siteId } = useContext(StaticPropsContext);
  const isMobile = deviceType === MOBILE;
  const inspectionBadge = getInspectionBadgeContent(tag_action, !!andes_tooltip);
  return (
    <>
      {!andes_tooltip ? (
        inspectionBadge
      ) : (
        <div
          key={`${namespace}-badge-container`}
          className={classNames(`${namespace}__inspection-badge-container`, { [`${deviceType}`]: isMobile })}
          data-testid="label-inspection-container"
        >
          {inspectionBadge}
          <Tooltip
            id="tooltip-desc"
            className={classNames(`${namespace}__inspection-badge-tooltip`, { [`${deviceType}`]: isMobile })}
            content={getInspectionTooltipContent(andes_tooltip.body, deviceType)}
            type={isMobile ? 'highlight' : 'light'}
            side={isMobile ? 'bottom' : 'left'}
            offsetY={isMobile ? 8 : 1}
            offsetX={getOffSetX(deviceType, siteId)}
            trigger="click"
            closable={andes_tooltip.closeable}
            data-testid="informative-tooltip"
          >
            <div className={`${namespace}__inspection-badge-icon-container`} data-testid="help-icon">
              {IconFactory({ id: icon.id }, `${namespace}__inspection-badge-icon`)}
            </div>
          </Tooltip>
        </div>
      )}

      {additional_description && (
        <StyledLabel className={`${namespace}__additional-description`} {...additional_description} />
      )}

      {campaign_link && (
        <div className={`${namespace}__campaign-link`}>
          <StyledLabel {...campaign_link.label} as="span" />
          {campaign_link.target && (
            <MediaAction
              className={`${namespace}__campaign-link-action`}
              label={campaign_link.label.values.blue_text}
              target={campaign_link.target}
              htmlTarget={campaign_link.html_target}
              accessibilityText={campaign_link.accessibility_text}
              onClick={() => {
                trackEvent(campaign_link.track);
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

InspectionVehiclesTag.propTypes = {
  tag_action: shape({
    action_type: string,
    label: shape({
      text: string.isRequired,
      color: string,
      bg_color: string,
      font_family: string,
    }),
    target: string,
  }),
  icon_action: shape({
    action_type: string,
    andes_tooltip: shape({
      id: string,
      state: string,
      closeable: bool,
      storable: bool,
      has_shadow: bool,
      change_color: bool,
      offsetX: number,
      type: string,
      trigger: string,
      body: shape({
        text: string,
        color: string,
        font_size: string,
        font_family: string,
      }),
    }),
    icon: shape({
      id: string,
      size: string,
    }),
  }),
  additional_description: shape({
    text: string,
    color: string,
    bg_color: string,
    font_size: string,
    font_family: string,
  }),
  additional_info: shape({
    text: string,
    color: string,
    bg_color: string,
    font_size: string,
    font_family: string,
  }),
  campaign_link: shape({
    target: string,
    html_target: string,
    accessibility_text: string,
    timeout: number,
    duration: number,
    label: shape({
      text: string.isRequired,
      color: string,
      font_size: string,
      font_family: string,
      values: shape({
        blue_text: shape({
          text: string,
          color: string,
          font_size: string,
          font_family: string,
        }),
      }),
    }),
    track: shape({
      melidata_event: shape({
        path: string,
        event_data: shape({
          item_id: string,
          category_id: string,
        }),
      }),
    }),
    action_type: string,
  }),
};

export default memo(InspectionVehiclesTag);
